import { useCallback, useMemo, useState } from 'react'
import nunjucks from 'nunjucks'
import { ApiClientV1 } from '../../core/api'
import { usePage, useUser } from '../../core/hooks'
import { PlatformError, throwPlatformError } from '../../errors/utils/errors'
import { DEFAULT_TEMPLATE_NAME } from '../constants'
import { createNunjucksEnvironment } from '../helpers'

/**
 * Initialises a Nunjucks template environment. Passes a `getSource` callback to
 * the env allowing it to fetch templates where necessary.
 *
 * When templates are fetched their UIDs are held in state and returned along
 * with the env. This allows the template UIDs to be passed into context and to
 * error boundaries where needed.
 *
 * @param apiClient An APIClient instance
 * @param templateName The name of a template to fetch
 * @param content An inline template string, if not using a templateName
 * @param currentUser A data of currently logged in user at the time
 */
export const useNunjucksEnvironment = (
  apiClient: ApiClientV1 | null,
  {
    templateName,
    content,
  }: {
    templateName?: string
    content?: string
  },
) => {
  const [templateUid, setTemplateUid] = useState<string>()
  const { user } = useUser()
  const { page } = usePage()

  const getSource = useCallback(
    async (
      name = templateName,
      callback: nunjucks.Callback<Error, nunjucks.LoaderSource>,
    ) => {
      if (!(templateName || content))
        return callback(
          new PlatformError('Could not find a template or templateName'),
          null,
        )

      if (content && name === DEFAULT_TEMPLATE_NAME) {
        return callback(null, { src: content, path: name, noCache: false })
      } else {
        try {
          if (!apiClient)
            return throwPlatformError('Could not find an API Client')

          if (!name) return throwPlatformError('Could not find a template name')

          const templateResponse =
            await apiClient.ui.template.getBestTemplate(name)

          if (!templateResponse)
            return throwPlatformError(`Template not found: ${templateName}`)

          setTemplateUid(templateResponse.uid)

          return callback(null, {
            src: templateResponse.content,
            path: name,
            noCache: false,
          })
        } catch (error) {
          callback(error as Error, null)
        }
      }
    },
    [apiClient, content, templateName],
  )

  const env = useMemo(() => {
    // Wait until the user is available as we always want to provide it in the
    // template context
    if (!user) return null

    return createNunjucksEnvironment(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- getSource is not expected to be async - Must be a better way around this?
      // @ts-ignore
      { async: true, getSource },
      {
        userId: apiClient?.userId,
        tenantId: apiClient?.tenantId,
        isSalesforce: apiClient?.isSalesforce,
        isSkedulo: apiClient?.isSkedulo,
        isServiceNow: apiClient?.isServiceNow,
      },
      user,
      page,
    )
    // window.location.search is in dependencies to get latest query params in Nunjucks
  }, [apiClient, getSource, user, page, window.location.search])

  return { env, templateUid }
}
